/**
 * FREESHIP notices during Whack Friday drops (1 Nov 2026, Skye).
 *
 * Framework-free ES module, loaded from snippets/rogue-freeship-state.liquid,
 * which also prints the switch (auto, on or off) and the drop times as epoch
 * seconds into script[data-rogue-freeship-data]. The server already set
 * <html data-rogue-freeship> when it built the page; this sets it again from
 * the shopper's clock, because Shopify can serve a page it cached before a
 * drop started, and flips it at the exact second a drop starts or ends while
 * the page is open. CSS in rogue-theme.css does the hiding (.rogue-fs-on,
 * .rogue-fs-off); the offer pop-up and the post-add sheet read the attribute.
 *
 * It also keeps <html data-rogue-drop> (live or off) right: whether a drop is
 * live by the times alone, whatever the FREESHIP switch says. The post-add
 * sheet reads it (3 Nov 2026, Black Friday review F3).
 *
 * Unit tested by tests/rogue-freeship.test.js.
 */

export const FREESHIP_ATTRIBUTE = 'data-rogue-freeship';
export const DROP_ATTRIBUTE = 'data-rogue-drop';

/** Long waits are re-checked rather than trusted to one timer (and setTimeout tops out at about 24 days). */
const MAX_WAIT_MS = 6 * 60 * 60 * 1000;

/**
 * Whether the FREESHIP notices show at `nowMs`.
 * @param {string} mode - auto, on or off
 * @param {Array<[number, number]>} windows - drop start and end, epoch seconds, end inclusive
 * @param {number} nowMs
 * @returns {'on'|'off'}
 */
export function freeshipState(mode, windows, nowMs) {
  if (mode === 'off') return 'off';
  if (mode === 'on') return 'on';
  return dropState(windows, nowMs) === 'live' ? 'off' : 'on';
}

/**
 * Whether a Whack Friday drop is live at `nowMs`, by the drop times alone.
 * @param {Array<[number, number]>} windows - drop start and end, epoch seconds, end inclusive
 * @param {number} nowMs
 * @returns {'live'|'off'}
 */
export function dropState(windows, nowMs) {
  const now = nowMs / 1000;
  return validWindows(windows).some(([start, end]) => now >= start && now <= end) ? 'live' : 'off';
}

/**
 * Milliseconds until the state can next change (a drop starting, or the second after one ends), or null.
 * @param {string} mode
 * @param {Array<[number, number]>} windows
 * @param {number} nowMs
 * @returns {number|null}
 */
export function msUntilNextChange(mode, windows, nowMs) {
  if (mode === 'off' || mode === 'on') return null;
  const now = nowMs / 1000;
  let next = null;
  for (const [start, end] of validWindows(windows)) {
    for (const edge of [start, end + 1]) {
      if (edge > now && (next === null || edge < next)) next = edge;
    }
  }
  return next === null ? null : Math.ceil((next - now) * 1000);
}

function validWindows(windows) {
  return (Array.isArray(windows) ? windows : []).filter(
    (w) => Array.isArray(w) && Number.isFinite(w[0]) && Number.isFinite(w[1]) && w[1] >= w[0]
  );
}

// Browser only: Node (the unit tests) has no document.
if (typeof document !== 'undefined') {
  let timer;
  const apply = () => {
    let data;
    try {
      data = JSON.parse(document.querySelector('script[data-rogue-freeship-data]')?.textContent || 'null');
    } catch {
      data = null;
    }
    if (!data) return;
    const mode = data.mode || 'auto';
    const now = Date.now();
    document.documentElement.setAttribute(FREESHIP_ATTRIBUTE, freeshipState(mode, data.windows, now));
    document.documentElement.setAttribute(DROP_ATTRIBUTE, dropState(data.windows, now));
    window.clearTimeout(timer);
    // The drop state follows the times whatever the switch says, so wait for the next edge in any mode.
    const wait = msUntilNextChange('auto', data.windows, now);
    if (wait !== null) timer = window.setTimeout(apply, Math.min(wait + 50, MAX_WAIT_MS));
  };
  apply();
  // Timers sleep in background tabs and a page can come back from the back-forward cache.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) apply();
  });
  window.addEventListener('pageshow', apply);
}
